import React, { useMemo, useState } from "react";
import { Info } from "lucide-react";
import { getLateSlabs, getEarlySlabs, sumLate, sumEarly } from "@/lib/attendanceShifts";

/**
 * Cohort heatmap for attendance.
 * Rows = employees, Columns = months.
 * Cell intensity = severity score (weighted: after_1030 ×2, before_1800 ×2, others ×1).
 *
 * Props:
 *   records: [{user_id, employee_name, department_name, month, ...bucket counts}]
 *   months: ["YYYY-MM", ...] ascending
 *   onCellClick?: (record) => void
 */

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const monthLabel = (ym) => { if (!ym) return ""; const [y,m] = ym.split("-"); return `${MONTH_NAMES[parseInt(m,10)-1]} '${y.slice(2)}`; };

const severityScore = (r) => {
    if (!r) return 0;
    const [l1, l2, l3] = getLateSlabs(r);
    const [e1, e2, e3] = getEarlySlabs(r);
    // Third slab (after grace / very early) weighted 2x — same weighting
    // as before, now shift-agnostic so firmware records score fairly.
    return l1 + l2 + l3 * 2 + e1 + e2 + e3 * 2;
};

const intensityColor = (score, max) => {
    if (!score || score <= 0) return "#EEF0F2";
    const ratio = Math.min(1, score / (max || 1));
    // Teal → amber → red gradient based on severity ratio
    if (ratio <= 0.33) {
        // Teal (good-ish tint, subtle)
        const alpha = 0.18 + ratio * 0.5; // 0.18 → 0.35
        return `rgba(46, 180, 172, ${alpha.toFixed(2)})`;
    }
    if (ratio <= 0.66) {
        // Amber
        const alpha = 0.35 + (ratio - 0.33) * 0.6;
        return `rgba(234, 179, 8, ${alpha.toFixed(2)})`;
    }
    // Red
    const alpha = 0.5 + (ratio - 0.66) * 1.2;
    return `rgba(204, 59, 59, ${Math.min(1, alpha).toFixed(2)})`;
};

const AttendanceHeatmap = ({ records, months, onCellClick }) => {
    const [hovered, setHovered] = useState(null); // {rec, x, y}

    // Build [user][month] lookup
    const { rows, totals, max } = useMemo(() => {
        const byUser = new Map();
        let m = 0;
        for (const r of records || []) {
            if (!byUser.has(r.user_id)) {
                byUser.set(r.user_id, {
                    user_id: r.user_id,
                    name: r.employee_name,
                    dept: r.department_name || "—",
                    cells: {},
                    // Severity drives the cell colour intensity (weighted)
                    total: 0,
                    // Sum of raw `total_instances` shown in the side column
                    // so the heatmap matches the table's "Total" column.
                    rawTotal: 0,
                });
            }
            const entry = byUser.get(r.user_id);
            entry.cells[r.month] = r;
            const s = severityScore(r);
            entry.total += s;
            entry.rawTotal += r.total_instances || 0;
            if (s > m) m = s;
        }
        const rows = Array.from(byUser.values()).sort((a, b) => b.total - a.total);
        // Per-month totals — sum of plain `total_instances` so the footer
        // row matches the side column and the main table's numbers.
        const totals = {};
        for (const mo of months) {
            totals[mo] = 0;
            for (const u of rows) totals[mo] += u.cells[mo]?.total_instances || 0;
        }
        return { rows, totals, max: m || 1 };
    }, [records, months]);

    if (!months || months.length === 0 || rows.length === 0) return null;

    const handleEnter = (e, r) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setHovered({ rec: r, x: rect.left + rect.width / 2, y: rect.top });
    };

    return (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 relative" data-testid="attendance-heatmap">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                    <h3 className="font-heading text-lg font-semibold text-slate-900">Severity heatmap</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Rows sorted by highest total flags. Darker = more attention needed.</p>
                </div>
                <Legend max={max} />
            </div>

            <div className="overflow-x-auto scrollbar-thin">
                <table className="border-separate" style={{ borderSpacing: "3px" }}>
                    <thead>
                        <tr>
                            <th className="sticky left-0 bg-white text-left text-[10px] uppercase tracking-wider font-semibold text-slate-400 pr-3 pb-2 align-bottom" style={{ minWidth: 180 }}>
                                <span className="inline-flex items-center gap-1">
                                    <Info className="h-3 w-3" /> Employee
                                </span>
                            </th>
                            {months.map((m) => (
                                <th
                                    key={m}
                                    className="text-center text-[10px] font-semibold text-slate-500 pb-2"
                                    style={{ minWidth: 32 }}
                                >
                                    {monthLabel(m)}
                                </th>
                            ))}
                            <th
                                className="text-right text-[10px] uppercase tracking-wider font-semibold text-slate-400 pl-3 pb-2 align-bottom"
                                title="Sum of every late + early instance across all visible months"
                            >
                                Total
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((u) => (
                            <tr key={u.user_id}>
                                <td className="sticky left-0 bg-white pr-3 py-0.5 align-middle">
                                    <div className="flex items-center gap-2">
                                        <div className="h-6 w-6 shrink-0 rounded-full bg-teal-50 text-[#25908A] border border-teal-100 flex items-center justify-center text-[10px] font-bold uppercase">
                                            {(u.name || "?").charAt(0)}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-xs font-medium text-slate-900 truncate" style={{ maxWidth: 140 }}>{u.name}</div>
                                            <div className="text-[10px] text-slate-400 truncate" style={{ maxWidth: 140 }}>{u.dept}</div>
                                        </div>
                                    </div>
                                </td>
                                {months.map((m) => {
                                    const rec = u.cells[m];
                                    const s = severityScore(rec);
                                    const color = intensityColor(s, max);
                                    return (
                                        <td key={m} className="p-0 align-middle">
                                            <div
                                                role={rec ? "button" : undefined}
                                                tabIndex={rec ? 0 : -1}
                                                onClick={() => rec && onCellClick?.(rec)}
                                                onMouseEnter={(e) => rec && handleEnter(e, rec)}
                                                onMouseLeave={() => setHovered(null)}
                                                data-testid={rec ? `heatmap-cell-${rec.user_id}-${m}` : undefined}
                                                className={`h-6 w-6 rounded-[3px] transition-transform ${rec ? "cursor-pointer hover:scale-125 hover:ring-2 hover:ring-[#2EB4AC] hover:ring-offset-1" : "opacity-60"}`}
                                                style={{ backgroundColor: color, border: !rec ? "1px dashed #CBD5E1" : "none" }}
                                                aria-label={rec ? `${u.name} ${monthLabel(m)} severity ${s}` : `${u.name} ${monthLabel(m)} no data`}
                                            />
                                        </td>
                                    );
                                })}
                                <td className="pl-3 text-right align-middle">
                                    <span
                                        className="text-xs font-bold tabular-nums text-slate-700"
                                        title={`Severity score (weighted): ${u.total}`}
                                    >
                                        {u.rawTotal}
                                    </span>
                                </td>
                            </tr>
                        ))}
                        {/* Column totals row */}
                        <tr>
                            <td className="sticky left-0 bg-white pr-3 pt-3 text-[10px] uppercase tracking-wider font-semibold text-slate-400">Total</td>
                            {months.map((m) => (
                                <td key={m} className="text-center pt-3">
                                    <span className="text-[10px] text-slate-500 tabular-nums">{totals[m] || 0}</span>
                                </td>
                            ))}
                            <td />
                        </tr>
                    </tbody>
                </table>
            </div>

            {hovered?.rec && (
                <div
                    className="pointer-events-none fixed z-50 bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs min-w-[180px]"
                    style={{
                        left: Math.max(16, Math.min(window.innerWidth - 220, hovered.x - 100)),
                        top: hovered.y - 110,
                    }}
                >
                    <div className="font-semibold text-slate-900">{hovered.rec.employee_name}</div>
                    <div className="text-slate-500 text-[10px] mb-1">{monthLabel(hovered.rec.month)}</div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
                        <span className="text-slate-500">Total</span><span className="text-right font-semibold tabular-nums">{hovered.rec.total_instances || 0}</span>
                        <span className="text-slate-500">Late entries</span><span className="text-right tabular-nums">{sumLate(hovered.rec)}</span>
                        <span className="text-slate-500">Early exits</span><span className="text-right tabular-nums">{sumEarly(hovered.rec)}</span>
                        <span className="text-slate-500">WFH</span><span className="text-right tabular-nums">{hovered.rec.wfh || 0}</span>
                        <span className="text-slate-500">OFF</span><span className="text-right tabular-nums">{hovered.rec.oof || 0}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

const Legend = ({ max }) => (
    <div className="inline-flex items-center gap-2 text-[10px] text-slate-500">
        <span>Low</span>
        <div className="flex gap-0.5">
            {[0.05, 0.25, 0.45, 0.65, 0.85].map((r, i) => (
                <div key={i} className="h-3 w-3 rounded-[2px]" style={{ backgroundColor: intensityColor(r * max, max) }} />
            ))}
        </div>
        <span>High</span>
    </div>
);

export default AttendanceHeatmap;
